import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI, PORTAL_URL } from '../utils/authAPI';

const AuthContext = createContext(null);

// Vassago no tiene login propio: la identidad la da Authentik vía OIDC
// (ONBOARDING-nueva-app.md §1, regla dura #3). Este contexto ya no maneja credenciales —
// solo lee la sesión y, si no hay, manda al usuario al handshake.
export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);

  // Redirige al authorize. Con sesión viva en el portal el salto es invisible; si no la hay,
  // el backend rebota a Pandemonium (que es la única puerta de entrada de la plataforma).
  const startLogin = useCallback(() => {
    const returnTo = window.location.pathname + window.location.search;
    window.location.assign(`/api/auth/login?returnTo=${encodeURIComponent(returnTo)}`);
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const session = await authAPI.getSession();
      if (session.authenticated) {
        setAuthenticated(true);
        setCurrentUser(session.user);
        setAuthLoading(false);
      } else {
        // Sin sesión no se pinta nada: se sale directo al SSO. `authLoading` se queda en true
        // a propósito para que la UI no parpadee mientras el navegador navega fuera.
        startLogin();
      }
    } catch {
      setAuthenticated(false);
      setCurrentUser(null);
      setAuthLoading(false);
    }
  }, [startLogin]);

  // "Salir" NO cierra sesión (regla dura #4): es volver al hub, manteniendo el SSO vivo para el
  // resto de las apps. El logout real vive solo en Pandemonium.
  const handleLogout = () => {
    window.location.assign(PORTAL_URL);
  };

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{
      authenticated,
      currentUser,
      authLoading,
      startLogin,
      handleLogout,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
